import React, { useEffect, useState, ChangeEvent } from "react";
import { responseHero } from "../../../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { MiningMachine } from "../../../../models/miningmachine.model";
import { MiningProduction } from "../../../../models/miningproduction.model";
import { UserGroups } from "../../../../models/usergroups.model";
import { getUsersInGroup } from "../../../../api/getUsersInGroup";
import { selectMenuStates } from "../../../../store/reducers/menuSlice";
import { useSelector } from "react-redux";

type ModalProps = {
  title: string;
  showModal: boolean;
  closeModal: () => void;
  handleStateChange: () => void;
  miningMachine?: MiningMachine;
  row?: MiningProduction;
};

type ModifiedPayload = Pick<MiningProduction, "amount" | "date" | "customDistribution" | "miningMachineId">;

const distributionOptions = [
  {
    label: "Hagyományos elosztás a csoportba érkezett befizetések alapján",
    value: false,
  },
  {
    label: "A termelt mennyiség 50-50 -ben való elosztása a Human D.C-vel",
    value: true,
  },
];

const ModifyMiningMachineModal = ({
  title,
  showModal,
  closeModal,
  handleStateChange,
  miningMachine,
  row,
}: ModalProps) => {
  const [recordData, setRecordData] = useState<ModifiedPayload>({
    amount: "",
    date: new Date(),
    customDistribution: false,
    miningMachineId: -1,
  });

  const menu = useSelector(selectMenuStates);

  const [members, setMembers] = useState<UserGroups[]>([]);
  const [selectedMember, setSelectedMember] = useState<number>(-1);

  useEffect(() => {
    if (!miningMachine || !row) return;

    setRecordData({
      amount: row.amount,
      date: new Date(row.date),
      customDistribution: row.customDistribution,
      miningMachineId: row.miningMachineId,
    });
  }, [miningMachine, row]);

  useEffect(() => {
    if (!menu?.daisyMenu?.group_id) return;
  
    const fetchMembers = async () => {
      try {
        const res = await getUsersInGroup(menu?.daisyMenu?.group_id || -1);
        setMembers(res);
      } catch (e) {
        console.error(e);
      }
    };
  
    fetchMembers();
  }, [menu?.daisyMenu?.group_id]);
  

  const handleChange = (field: keyof ModifiedPayload, value: string) => {
    setRecordData((prev) => {
      if (field === "amount") {
        if (!/^(\d*\.)?\d{0,8}$/.test(value)) return prev;
        return { ...prev, amount: value };
      }

      if (field === "date") {
        return { ...prev, date: new Date(value) };
      }

      return { ...prev, [field]: parseInt(value, 10) };
    });
  };



  const handleDistributionChange = (isCustom: boolean) => {
    setRecordData((prev) => ({
      ...prev,
      customDistribution: isCustom,
    }));
  };

  const handleSubmit = async () => {
    try {
      await responseHero.put(`${process.env.REACT_APP_API_URL}/miningProd/${row?.id}`, {
        miningProduction: recordData,
        selectedMember: selectedMember,
      });
      toast.success("Sikeresen frissítettél egy termelési adatsort!");
      handleStateChange();
      closeModal();
    } catch (error: any) {
      console.error(JSON.stringify(error?.response?.data?.message, null, 2));
      toast.error("Sikertelen adatrögzítés!");
      closeModal();
    }
  };

  const formatDateInput = (date: Date | string) => {
    return new Date(date).toISOString().split("T")[0];
  };

  if (!showModal) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden">
        <div className="relative w-full max-w-4xl p-4">
          <div className="relative flex flex-col w-full bg-white rounded-lg shadow-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-300">
              <h3 className="text-xl font-semibold text-brown">
                <span className="text-dark-blue">BÁNYAGÉP</span> - {title}
              </h3>
            </div>
            <div className="p-6">
              <form>
                <div className="flex p-2 space-x-4 rounded shadow-md">
                  <div className="w-1/2 text-center">
                    <label className="block mt-5 text-sm font-bold text-brown">Rögzítés dátuma</label>
                    <input
                      type="date"
                      value={formatDateInput(recordData.date)}
                      onChange={(e) => handleChange("date", e.target.value)}
                      className="w-full px-3 py-2 mt-2 border rounded shadow appearance-none focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  <div className="w-1/2 text-center">
                    <label className="block mt-5 text-sm font-bold text-brown">Termelt mennyiség (BTC)</label>
                    <input
                      type="text"
                      value={recordData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      className="w-full px-3 py-2 mt-2 border rounded shadow appearance-none focus:outline-none focus:shadow-outline"
                      placeholder="Coin mennyiség (BTC)"
                    />
                  </div>
                </div>
                <div className="p-5">
                  <label className="block mb-4 text-sm font-bold text-brown text-center">Részesedés elosztása</label>
                  <div className="space-y-2">
                    {distributionOptions.map((opt) => (
                      <label key={opt.label} className="inline-flex items-center">
                        <input
                          type="radio"
                          name="distribution"
                          value={opt.label}
                          checked={recordData.customDistribution === opt.value}
                          onChange={() => handleDistributionChange(opt.value)}
                          className="w-5 h-5 text-blue-600 form-radio"
                        />
                        <span className="ml-2 text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {recordData.customDistribution === false && (
                    <div className="mt-4">
                        <label className="block mb-2 text-sm font-bold text-brown text-center">Költség arányosítás tagok között</label>
                        <select
                        onChange={(e) => setSelectedMember(parseInt(e.target.value))}
                        value={selectedMember}
                        className="w-full bg-dark-blue text-white px-3 py-2 rounded-lg shadow-md"
                        >
                        <option value={-1} className="px-2 py-1 text-center">
                            Mindenkire vonatkozik arányosan
                        </option>
                        {members.map((member) => (
                            <option
                            key={member.user_id}
                            value={member.user_id}
                            className="px-2 py-1 text-center"
                            >
                            {member.user.name}
                            </option>
                        ))}
                        </select>
                    </div>
                )}

              </form>
            </div>
            <div className="flex justify-end p-6 border-t">
              <button
                type="button"
                onClick={closeModal}
                className="px-6 py-2 mr-2 text-sm font-bold text-red-500 uppercase"
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-3 text-sm font-bold text-cream uppercase bg-dark-blue rounded shadow hover:shadow-lg"
              >
                Hozzáad
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
    </>
  );
};

export default ModifyMiningMachineModal;
